import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import { Package, Plus, Pencil, Trash2, X, AlertTriangle, Boxes } from "lucide-react";

const CATEGORIES = [
  ["fardamento", "Fardamento"],
  ["equipamento", "Equipamento"],
  ["adereco", "Adereço"],
];
const TYPES = [
  ["reutilizavel", "Reutilizável (devolve-se)"],
  ["consumivel", "Consumível (gasta-se)"],
];
const catLabel = (k) => (CATEGORIES.find((c) => c[0] === k) || [k, k])[1];

const empty = { name: "", category: "fardamento", variant: "", type: "reutilizavel", total_qty: 0, low_stock_threshold: "", notes: "" };

export default function Stocks() {
  const { role } = useRole();
  const canEdit = role === "admin" || role === "gestor" || role === "superadmin";
  const [items, setItems] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'new'|'edit', form}
  const [showDisc, setShowDisc] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/stock/items").catch(() => ({ data: [] })),
      api.get("/stock/discrepancies").catch(() => ({ data: [] })),
    ]).then(([i, d]) => { setItems(i.data || []); setDiscrepancies(d.data || []); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setModal({ mode: "new", form: { ...empty } });
  const openEdit = (it) => setModal({ mode: "edit", id: it.id, form: { ...empty, ...it, low_stock_threshold: it.low_stock_threshold ?? "" } });

  const save = async () => {
    const f = modal.form;
    if (!f.name.trim()) { toast.error("Indica o nome do artigo"); return; }
    const payload = {
      name: f.name.trim(), category: f.category, variant: f.variant?.trim() || null, type: f.type,
      total_qty: parseInt(f.total_qty) || 0,
      low_stock_threshold: f.low_stock_threshold === "" ? null : parseInt(f.low_stock_threshold),
      notes: f.notes?.trim() || null,
    };
    try {
      if (modal.mode === "new") await api.post("/stock/items", payload);
      else await api.put(`/stock/items/${modal.id}`, payload);
      toast.success(modal.mode === "new" ? "Artigo criado" : "Artigo atualizado");
      setModal(null); load();
    } catch (e) { toast.error("Erro", { description: e?.response?.data?.detail || e.message }); }
  };

  const del = async (it) => {
    if (!window.confirm(`Eliminar "${it.name}${it.variant ? " " + it.variant : ""}"?`)) return;
    try { await api.delete(`/stock/items/${it.id}`); toast.success("Artigo eliminado"); load(); }
    catch (e) { toast.error("Não foi possível eliminar", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div className="space-y-6" data-testid="stocks-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold text-zinc-950 flex items-center gap-2"><Boxes className="w-7 h-7" /> Stocks</h1>
          <p className="text-sm text-zinc-500 mt-1">Fardamento, equipamento e adereços — atribui a eventos e controla devoluções.</p>
        </div>
        {canEdit && (
          <button onClick={openNew} data-testid="new-stock-item" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
            <Plus className="w-4 h-4" /> Novo artigo
          </button>
        )}
      </div>

      {discrepancies.length > 0 && (
        <button onClick={() => setShowDisc((s) => !s)} data-testid="discrepancies-toggle"
          className="w-full text-left bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">{discrepancies.length} artigo(s) por devolver / em falta</span>
          <span className="ml-auto text-xs text-amber-700 underline">{showDisc ? "ocultar" : "ver detalhe"}</span>
        </button>
      )}
      {showDisc && discrepancies.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100" data-testid="discrepancies-list">
          {discrepancies.map((d) => (
            <div key={d.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <span className="font-medium text-zinc-900">{d.item_name}{d.variant ? ` ${d.variant}` : ""}</span>
              <span className="text-zinc-500">{d.event_name || "—"}</span>
              {d.promoter_name && <span className="text-zinc-700">· {d.promoter_name}</span>}
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
                {d.lost_qty > 0 ? `${d.lost_qty} ${d.lost_reason || "em falta"}` : `${d.pending_qty} por devolver`}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
            <tr>
              <th className="text-left py-2.5 px-4">Artigo</th>
              <th className="text-left py-2.5 px-4">Categoria</th>
              <th className="text-left py-2.5 px-4">Tipo</th>
              <th className="text-right py-2.5 px-4">Total</th>
              <th className="text-right py-2.5 px-4">Fora</th>
              <th className="text-right py-2.5 px-4">Disponível</th>
              {canEdit && <th className="py-2.5 px-4"></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">A carregar…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400">Sem artigos. Cria o primeiro.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`stock-row-${it.id}`} className="border-b border-zinc-100">
                <td className="py-2.5 px-4">
                  <span className="font-medium text-zinc-900">{it.name}</span>
                  {it.variant && <span className="ml-1.5 text-xs font-semibold text-zinc-500 bg-zinc-100 rounded px-1.5 py-0.5">{it.variant}</span>}
                </td>
                <td className="py-2.5 px-4 text-zinc-600">{catLabel(it.category)}</td>
                <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full border ${it.type === "consumivel" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{it.type === "consumivel" ? "Consumível" : "Reutilizável"}</span></td>
                <td className="py-2.5 px-4 text-right text-zinc-700">{it.total_qty}</td>
                <td className={`py-2.5 px-4 text-right ${it.out_qty > 0 ? "text-amber-700" : "text-zinc-400"}`}>{it.out_qty}</td>
                <td className={`py-2.5 px-4 text-right font-semibold ${it.low_stock ? "text-red-600" : "text-emerald-700"}`}>
                  {it.available_qty}{it.low_stock && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 -mt-0.5" />}
                </td>
                {canEdit && (
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} data-testid={`edit-stock-${it.id}`} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(it)} data-testid={`delete-stock-${it.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="stock-item-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <h3 className="text-base font-display font-bold flex items-center gap-2"><Package className="w-5 h-5" /> {modal.mode === "new" ? "Novo artigo" : "Editar artigo"}</h3>
              <button onClick={() => setModal(null)} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-zinc-500">Nome</label>
                  <input value={modal.form.name} onChange={(e) => setModal({ ...modal, form: { ...modal.form, name: e.target.value } })} data-testid="stock-name" placeholder="T-shirt Branca" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500">Tamanho/Variante</label>
                  <input value={modal.form.variant} onChange={(e) => setModal({ ...modal, form: { ...modal.form, variant: e.target.value } })} data-testid="stock-variant" placeholder="M" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500">Categoria</label>
                  <select value={modal.form.category} onChange={(e) => setModal({ ...modal, form: { ...modal.form, category: e.target.value } })} data-testid="stock-category" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm">
                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500">Tipo</label>
                  <select value={modal.form.type} onChange={(e) => setModal({ ...modal, form: { ...modal.form, type: e.target.value } })} data-testid="stock-type" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm">
                    {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500">Quantidade total</label>
                  <input type="number" min="0" value={modal.form.total_qty} onChange={(e) => setModal({ ...modal, form: { ...modal.form, total_qty: e.target.value } })} data-testid="stock-total" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500">Alerta stock baixo (opcional)</label>
                  <input type="number" min="0" value={modal.form.low_stock_threshold} onChange={(e) => setModal({ ...modal, form: { ...modal.form, low_stock_threshold: e.target.value } })} data-testid="stock-threshold" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500">Notas</label>
                <textarea value={modal.form.notes} onChange={(e) => setModal({ ...modal, form: { ...modal.form, notes: e.target.value } })} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm">Cancelar</button>
              <button onClick={save} data-testid="save-stock-item" className="px-4 py-2 rounded-lg bg-zinc-950 text-white text-sm font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
